import React, { useCallback, useMemo, ReactElement } from 'react';
import ListItemText from '../list-item-text/list-item-text.component';
import ListItemIcon from '../list-item-icon/list-item-icon.component';
import Menu from '../menu/menu.component';
import MenuItem from '../menu-item/menu-item.component';
import CheckAccess from '../check-access/check-access.component';
import FormatService from '../../services/format-service';
import useStylesContextMenu from './context-menu.styles';

const ContextMenu: ReactMemoComponent = React.memo((props: ReactComponentProps): ReactElement => {
  const {
    menuAnchor,
    actionMenuItems,
    dataAttributeEntityId,
    dataAttributeEntityType,
    dataAttributeExtraEntityType,
    className,
    onClose,
    onCheckIsMenuItemVisible,
  } = props;

  const { classes } = useStylesContextMenu();

  const withOpenItems: boolean = actionMenuItems.reduce((result: boolean, actionMenuItem: ActionMenuItem): boolean => (
    result || (!actionMenuItem.permission && !actionMenuItem.permissions)
  ), false);
  const actionMenuPermissions: string[] = useMemo((): string[] => (
    actionMenuItems && actionMenuItems.map((actionMenuItem: ActionMenuItem): string | string[] => (
      actionMenuItem.permission || actionMenuItem.permissions || []
    )).flat()
  ), [actionMenuItems]);
  const withVisibleItems: boolean = useMemo((): boolean => (
    actionMenuItems ? actionMenuItems.reduce((
      withVisibleItemsResult: boolean, actionMenuItem: ActionMenuItem,
    ): boolean => {
      const linkedEntityType: string = menuAnchor && dataAttributeEntityType
        && menuAnchor.getAttribute(dataAttributeEntityType);
      const linkedExtraEntityType: string = menuAnchor && dataAttributeExtraEntityType
        && menuAnchor.getAttribute(dataAttributeExtraEntityType);

      return withVisibleItemsResult || (onCheckIsMenuItemVisible ? onCheckIsMenuItemVisible(
        actionMenuItem,
        dataAttributeEntityType ? linkedEntityType : null,
        dataAttributeExtraEntityType ? linkedExtraEntityType : null,
      ) : true);
    }, false) : false
  ), [actionMenuItems, menuAnchor, dataAttributeEntityType, dataAttributeExtraEntityType]);

  const onMenuItemClick: Function = useCallback((
    event: React.MouseEvent<Element, MouseEvent>,
    entityId?: number,
    onClickCallback?: Function,
  ): void => {
    if (onClickCallback) {
      onClickCallback(event, entityId || null);
    }

    if (onClose) {
      onClose(event);
    }
  }, []);

  return (
    <CheckAccess permissions={!withOpenItems ? actionMenuPermissions : []}>
      {
        withVisibleItems && (
          <Menu
            isFlat
            isBordered
            menuAnchor={menuAnchor}
            menuClassName={FormatService.formatClassNames(classes.contextMenu, className)}
            onClose={onClose}
          >
            {
              actionMenuItems.map((actionMenuItem: ActionMenuItem): ReactElement | boolean => {
                const linkedEntityId: number = (menuAnchor && dataAttributeEntityId
                  && FormatService.parseInt(menuAnchor.getAttribute(dataAttributeEntityId))) || null;
                const linkedEntityType: string = menuAnchor && dataAttributeEntityType
                  && menuAnchor.getAttribute(dataAttributeEntityType);
                const linkedExtraEntityType: string = menuAnchor && dataAttributeExtraEntityType
                  && menuAnchor.getAttribute(dataAttributeExtraEntityType);
                const isItemVisible: boolean = onCheckIsMenuItemVisible ? onCheckIsMenuItemVisible(
                  actionMenuItem,
                  dataAttributeEntityType ? linkedEntityType : null,
                  dataAttributeExtraEntityType ? linkedExtraEntityType : null,
                ) : true;
                const actionMenuItemPermissions: string[] = actionMenuItem.permission ? [actionMenuItem.permission]
                  : (actionMenuItem.permissions || []);

                return isItemVisible && (
                  <div key={actionMenuItem.id}>
                    <CheckAccess permissions={actionMenuItemPermissions}>
                      <MenuItem
                        onClick={(event: React.MouseEvent<Element, MouseEvent>): void => {
                          onMenuItemClick(event, linkedEntityId, actionMenuItem.onClick);
                        }}
                      >
                        <ListItemIcon className={classes.contextMenuItemIcon}>
                          {actionMenuItem.icon}
                        </ListItemIcon>
                        <ListItemText>
                          {actionMenuItem.text}
                        </ListItemText>
                      </MenuItem>
                    </CheckAccess>
                  </div>
                );
              })
            }
          </Menu>
        )
      }
    </CheckAccess>
  );
});

export default ContextMenu;
